import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { decryptData } from '../encryptionUtil';

// UK Tax Rates for Self Employed
const TAX_BANDS = {
  "2026-2027": {
    personalAllowance: 12570,
    basicRateThreshold: 50270,
    higherRateThreshold: 125140,
    basicRate: 0.20,
    higherRate: 0.40,
    additionalRate: 0.45,
    niClassTwo: 3.65,
    niClassFourLowerRate: 0.06,
    niClassFourUpperRate: 0.02,
    niClassFourLowerThreshold: 12570,
    niClassFourUpperThreshold: 50270
  },
  "2025-2026": {
    personalAllowance: 12570,
    basicRateThreshold: 50270,
    higherRateThreshold: 125140,
    basicRate: 0.20,
    higherRate: 0.40,
    additionalRate: 0.45,
    niClassTwo: 3.50,
    niClassFourLowerRate: 0.06,
    niClassFourUpperRate: 0.02,
    niClassFourLowerThreshold: 12570,
    niClassFourUpperThreshold: 50270
  },
  "2024-2025": {
    personalAllowance: 12570,
    basicRateThreshold: 50270,
    higherRateThreshold: 125140,
    basicRate: 0.20,
    higherRate: 0.40,
    additionalRate: 0.45,
    niClassTwo: 3.45,
    niClassFourLowerRate: 0.09,
    niClassFourUpperRate: 0.02,
    niClassFourLowerThreshold: 12570,
    niClassFourUpperThreshold: 50270
  },
  "2023-2024": {
    personalAllowance: 12570,
    basicRateThreshold: 50270,
    higherRateThreshold: 150000,
    basicRate: 0.20,
    higherRate: 0.40,
    additionalRate: 0.45,
    niClassTwo: 3.15,
    niClassFourLowerRate: 0.09,
    niClassFourUpperRate: 0.02,
    niClassFourLowerThreshold: 12570,
    niClassFourUpperThreshold: 50270
  },
  "2022-2023": {
    personalAllowance: 12570,
    basicRateThreshold: 50270,
    higherRateThreshold: 150000,
    basicRate: 0.20,
    higherRate: 0.40,
    additionalRate: 0.45,
    niClassTwo: 3.15,
    niClassFourLowerRate: 0.09,
    niClassFourUpperRate: 0.02,
    niClassFourLowerThreshold: 11908,
    niClassFourUpperThreshold: 50270
  }
};

// UK Tax Calendar - Important Deadlines
const TAX_CALENDAR_EVENTS = [
  { date: "31 January", description: "Self Assessment deadline: Final payment for previous tax year & first payment on account" },
  { date: "5 April", description: "End of tax year" },
  { date: "6 April", description: "Start of new tax year" },
  { date: "31 July", description: "Second payment on account due" },
  { date: "5 October", description: "Deadline to register for Self Assessment if not registered before" },
  { date: "30 December", description: "Online tax return deadline if you want HMRC to collect tax through your tax code" }
];

export const taxService = {
  // COPIED FROM REPORTS SERVICE: Get all invoice data for a user
  async getAllInvoiceData(userId, practiceId = 'all') {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService.getAllInvoiceData: Fetching invoices for user ${userId}, practice ${practiceId}`);
      }
      
      const invoicesRef = collection(db, 'invoices');
      let invoicesQuery;

      if (practiceId && practiceId !== 'all') {
        invoicesQuery = query(
          invoicesRef,
          where('userId', '==', userId),
          where('practiceId', '==', practiceId),
          orderBy('createdAt', 'desc')
        );
      } else {
        invoicesQuery = query(
          invoicesRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(invoicesQuery);
      const invoices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()      }));

      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService.getAllInvoiceData: Retrieved ${invoices.length} total invoices`);
      }
      return invoices;

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching invoice data:', error);
      }
      throw error;
    }
  },

  // Get tax year boundaries (April 6 to April 5)
  getTaxYearBoundaries(taxYearInput) {
    let startYear;
    
    // Handle both number and string inputs for consistency
    if (typeof taxYearInput === 'string') {
      // Parse "2024-2025" format
      startYear = parseInt(taxYearInput.split('-')[0], 10);
    } else if (typeof taxYearInput === 'number') {
      // Handle single year number
      startYear = taxYearInput;
    } else {
      throw new Error('Invalid tax year input. Expected number or "YYYY-YYYY" string format.');
    }
    
    // UK tax year: April 6 to April 5 next year
    const startDate = new Date(startYear, 3, 6, 0, 0, 0, 0); // April 6
    const endDate = new Date(startYear + 1, 3, 5, 23, 59, 59, 999); // April 5 next year
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Tax year ${taxYearInput}: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    }
    
    return { start: startDate, end: endDate };
  },
  // MAIN FUNCTION: Calculate tax summary using Reports logic
  async calculateTaxSummary(userId, taxYear) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService.calculateTaxSummary: Starting for user ${userId}, tax year ${taxYear}`);
      }
      
      // Step 1: Get ALL invoices (same as Reports)
      const allInvoices = await this.getAllInvoiceData(userId, 'all');
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService: Got ${allInvoices.length} total invoices`);
      }
      
      // Step 2: Filter deleted invoices (same as Reports.jsx)
      const activeInvoices = allInvoices.filter(inv => inv.status !== 'deleted');
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService: ${activeInvoices.length} active invoices after filtering deleted`);
      }
      
      // Step 3: Get tax year boundaries
      const { start: taxYearStart, end: taxYearEnd } = this.getTaxYearBoundaries(taxYear);
      
      // Step 4: Filter invoices by treatment dates within tax year (same logic as Reports)
      const taxYearInvoices = [];
      let totalIncome = 0;
      let totalEntries = 0;
      
      activeInvoices.forEach(invoice => {
        if (!Array.isArray(invoice.summaryData)) {
          return;
        }
        
        let invoiceIncluded = false;
        let invoiceIncome = 0;
        let invoiceEntries = 0;
        
        invoice.summaryData.forEach(entry => {
          // Parse treatment date
          let treatmentDate = null;
          if (entry.date) {
            if (typeof entry.date.toDate === 'function') {
              treatmentDate = entry.date.toDate();
            } else if (entry.date instanceof Date) {
              treatmentDate = entry.date;
            } else {
              treatmentDate = new Date(entry.date);
            }
          }
          
          // Check if treatment date is within tax year
          if (treatmentDate && treatmentDate >= taxYearStart && treatmentDate <= taxYearEnd) {
            if (!invoiceIncluded) {
              taxYearInvoices.push(invoice);
              invoiceIncluded = true;
            }
            
            const entryAmount = parseFloat(entry.amount) || 0;
            invoiceIncome += entryAmount;
            invoiceEntries++;
          }
        });
        
        totalIncome += invoiceIncome;        totalEntries += invoiceEntries;
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService: Found ${taxYearInvoices.length} invoices with treatment dates in ${taxYear}`);
        console.log(`taxService: Total income from tax year: £${totalIncome.toFixed(2)}`);
      }
      
      // Step 5: Get expenses for tax year (simplified for now)
      const expenses = await this.getExpensesForTaxYear(userId, taxYearStart, taxYearEnd);
      const totalExpenses = expenses.reduce((sum, exp) => sum + (parseFloat(exp.totalAmount) || 0), 0);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService: Total expenses: £${totalExpenses.toFixed(2)}`);
      }
      
      // Step 6: Calculate tax
      const taxableIncome = Math.max(0, totalIncome - totalExpenses);
      const incomeTaxResult = this.calculateIncomeTax(taxableIncome, taxYear);
      const nationalInsuranceResult = this.calculateNationalInsurance(taxableIncome, taxYear);
      
      const incomeTax = incomeTaxResult?.tax || 0;
      const nationalInsurance = nationalInsuranceResult?.ni || 0;
      const totalTax = incomeTax + nationalInsurance;
      
      const effectiveRate = taxableIncome > 0 ? 
        ((totalTax / taxableIncome) * 100).toFixed(1) : 0;
      
      // Step 7: Return results
      const result = {
        // Financial totals
        income: totalIncome,
        expenses: totalExpenses,
        taxableIncome: taxableIncome,
        incomeTax: incomeTax,
        nationalInsurance: nationalInsurance,
        totalTax: totalTax,
        effectiveRate: parseFloat(effectiveRate),
        netIncome: taxableIncome - totalTax,
          // Metadata
        taxYear: taxYear,
        taxYearStart: taxYearStart.toISOString(),
        taxYearEnd: taxYearEnd.toISOString(),
        invoiceCount: taxYearInvoices.length,
        entriesCount: totalEntries,        expenseCount: expenses.length,
        
        // Raw data for debugging
        invoices: taxYearInvoices,
        expenseRecords: expenses,
        
        // Tax calculation details
        incomeTaxDetails: incomeTaxResult,        nationalInsuranceDetails: nationalInsuranceResult
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('taxService: Tax summary complete:', {
          taxYear,
          invoiceCount: result.invoiceCount,
          income: result.income,
          expenses: result.expenses,
          totalTax: result.totalTax
        });
      }
      
      return result;
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error in taxService.calculateTaxSummary:', error);
      }
      throw new Error(`Tax calculation failed: ${error.message}`);
    }
  },

  // Get expenses for tax year
  async getExpensesForTaxYear(userId, startDate, endDate) {
    try {
      const expensesRef = collection(db, 'expenses');
      const expensesQuery = query(
        expensesRef,
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(expensesQuery);
      const allExpenses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter by date and exclude deleted
      const taxYearExpenses = allExpenses.filter(expense => {
        if (expense.status === 'deleted') return false;
        
        let expenseDate = null;
        if (expense.date) {
          if (typeof expense.date.toDate === 'function') {
            expenseDate = expense.date.toDate();
          } else if (expense.date instanceof Date) {
            expenseDate = expense.date;
          } else {
            expenseDate = new Date(expense.date);
          }
        }
          return expenseDate && expenseDate >= startDate && expenseDate <= endDate;
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`taxService: Found ${taxYearExpenses.length} expenses in tax year`);
      }
      return taxYearExpenses;
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching expenses:', error);
      }
      return [];
    }
  },

  // Calculate income tax (UNCHANGED - this works)
  calculateIncomeTax(income, taxYear = "2023-2024") {
    const rates = TAX_BANDS[taxYear];
      if (!rates) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Tax rates not found for year ${taxYear}, using 2023-2024`);
      }
      return this.calculateIncomeTax(income, "2023-2024");
    }
    
    const incomeNum = parseFloat(income) || 0;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Calculating income tax for £${incomeNum} in tax year ${taxYear}`);
    }
    
    let tax = 0;
    let remainingIncome = incomeNum;
    
    // Personal allowance
    let personalAllowance = rates.personalAllowance;
    if (incomeNum > 100000) {
      personalAllowance = Math.max(0, personalAllowance - ((incomeNum - 100000) / 2));
    }
      const personalAllowanceUsed = Math.min(remainingIncome, personalAllowance);
    remainingIncome -= personalAllowanceUsed;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Personal allowance used: £${personalAllowanceUsed}, tax-free`);
    }
    
    // Basic rate tax
    if (remainingIncome > 0) {      const basicRateTaxable = Math.min(remainingIncome, rates.basicRateThreshold - personalAllowance);
      tax += basicRateTaxable * rates.basicRate;
      remainingIncome -= basicRateTaxable;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Basic rate tax: £${basicRateTaxable} × ${rates.basicRate * 100}% = £${(basicRateTaxable * rates.basicRate).toFixed(2)}`);
      }
    }
    
    // Higher rate tax
    if (remainingIncome > 0) {      const higherRateTaxable = Math.min(remainingIncome, rates.higherRateThreshold - rates.basicRateThreshold);
      tax += higherRateTaxable * rates.higherRate;
      remainingIncome -= higherRateTaxable;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Higher rate tax: £${higherRateTaxable} × ${rates.higherRate * 100}% = £${(higherRateTaxable * rates.higherRate).toFixed(2)}`);
      }
    }
    
    // Additional rate tax
    if (remainingIncome > 0) {
      tax += remainingIncome * rates.additionalRate;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Additional rate tax: £${remainingIncome} × ${rates.additionalRate * 100}% = £${(remainingIncome * rates.additionalRate).toFixed(2)}`);
      }
    }
    
    const roundedTax = Math.round(tax * 100) / 100;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Total income tax: £${roundedTax}`);
    }
    
    return {
      income: incomeNum,
      taxYear,
      tax: roundedTax,
      effectiveRate: incomeNum > 0 ? Math.round((roundedTax / incomeNum) * 10000) / 100 : 0
    };
  },
  
  // Calculate National Insurance (UNCHANGED - this works)
  calculateNationalInsurance(income, taxYear = "2023-2024") {
    const rates = TAX_BANDS[taxYear];
      if (!rates) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Tax rates not found for year ${taxYear}, using 2023-2024`);
      }
      return this.calculateNationalInsurance(income, "2023-2024");
    }
    
    const incomeNum = parseFloat(income) || 0;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Calculating NI for £${incomeNum} in tax year ${taxYear}`);
    }
    
    const weeksInYear = 52;
    let ni = 0;
    
    // Class 2 NI
    const smallProfitsThreshold = 6725;    if (incomeNum >= smallProfitsThreshold) {
      ni += rates.niClassTwo * weeksInYear;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Class 2 NI: £${rates.niClassTwo} × ${weeksInYear} weeks = £${(rates.niClassTwo * weeksInYear).toFixed(2)}`);
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Income below small profits threshold - no Class 2 NI due`);
      }
    }
    
    // Class 4 NI
    const lowerThreshold = rates.niClassFourLowerThreshold;
    const upperThreshold = rates.niClassFourUpperThreshold;
      if (incomeNum > lowerThreshold) {
      const lowerRateAmount = Math.min(incomeNum - lowerThreshold, upperThreshold - lowerThreshold);
      ni += lowerRateAmount * rates.niClassFourLowerRate;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Class 4 NI (lower rate): £${lowerRateAmount} × ${rates.niClassFourLowerRate * 100}% = £${(lowerRateAmount * rates.niClassFourLowerRate).toFixed(2)}`);
      }
      
      if (incomeNum > upperThreshold) {
        const upperRateAmount = incomeNum - upperThreshold;
        ni += upperRateAmount * rates.niClassFourUpperRate;
        if (process.env.NODE_ENV === 'development') {
          console.log(`Class 4 NI (upper rate): £${upperRateAmount} × ${rates.niClassFourUpperRate * 100}% = £${(upperRateAmount * rates.niClassFourUpperRate).toFixed(2)}`);
        }
      }
    }
    
    const roundedNI = Math.round(ni * 100) / 100;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Total NI contributions: £${roundedNI}`);
    }
    
    return {
      income: incomeNum,
      taxYear,
      ni: roundedNI
    };
  },

  // Calculate tax liability (UNCHANGED - this works) 
  calculateTaxLiability(income, taxYear = "2023-2024", expenses = 0) {
    const incomeNum = parseFloat(income) || 0;
    const expensesNum = parseFloat(expenses) || 0;
    const taxableIncome = Math.max(0, incomeNum - expensesNum);
    
    const incomeTax = this.calculateIncomeTax(taxableIncome, taxYear);
    const ni = this.calculateNationalInsurance(taxableIncome, taxYear);
    
    return {
      income: incomeNum,
      expenses: expensesNum,
      taxableIncome: taxableIncome,
      taxYear,
      incomeTax: incomeTax.tax,
      nationalInsurance: ni.ni,
      totalTax: incomeTax.tax + ni.ni,
      effectiveRate: incomeNum > 0 ? Math.round(((incomeTax.tax + ni.ni) / incomeNum) * 10000) / 100 : 0
    };
  },

  // Get available tax years
  getAvailableTaxYears() {
    return Object.keys(TAX_BANDS).sort().reverse();
  },

  // Get current tax year
  getCurrentTaxYear() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    
    // UK tax year starts April 6
    if (currentMonth > 3 || (currentMonth === 3 && currentDay >= 6)) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  },

  // Get tax calendar events
  getTaxCalendarEvents() {
    const currentYear = new Date().getFullYear();
    
    // Map the events to include the actual dates for current and next year
    const eventsWithDates = TAX_CALENDAR_EVENTS.map(event => {
      // Parse the event date (format: "31 January" or "5 April")
      const [day, month] = event.date.split(' ');
      const monthIndex = this.getMonthIndex(month);
        if (monthIndex === -1) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Invalid month in event: ${event.date}`);
        }
        return null;
      }
      
      // Create dates for current year and next year
      return [
        {
          date: new Date(currentYear, monthIndex, parseInt(day)),
          description: event.description, // FIXED: Include description
          year: currentYear
        },
        {
          date: new Date(currentYear + 1, monthIndex, parseInt(day)),
          description: event.description, // FIXED: Include description
          year: currentYear + 1
        }
      ];
    }).filter(Boolean).flat();
    
    // Sort events by date
    return eventsWithDates.sort((a, b) => a.date - b.date);
  },

  // Helper function to convert month name to index
  getMonthIndex(month) {
    const months = {
      'January': 0,
      'February': 1,
      'March': 2,
      'April': 3,
      'May': 4,
      'June': 5,
      'July': 6,
      'August': 7,
      'September': 8,
      'October': 9,
      'November': 10,
      'December': 11
    };
    return months[month] !== undefined ? months[month] : -1;
  },

  // Helper function to decrypt user profile data
  decryptUserProfile(userInfo) {
    if (!userInfo) return userInfo;
    
    console.log('Decrypting user profile data for PDF...');
    
    // Fields that are typically encrypted in the profile
    const sensitiveFields = [
      'name', 'displayName', 'address1', 'address2', 'address3', 'address4', 
      'city', 'postcode', 'phone', 'bankName', 'accountNumber', 'sortCode',
      'gdcNumber', 'insurancePolicyNumber', 'insuranceProvider'
    ];
    
    const decrypted = { ...userInfo };
    let fieldsDecrypted = 0;
    
    // Handle the combined address field specially
    if (decrypted.address && typeof decrypted.address === 'string') {
      console.log('Processing combined address field...');
      const addressLines = decrypted.address.split('\n');
      const decryptedAddressLines = [];
      
      addressLines.forEach(line => {
        if (line && line.trim() && line.startsWith('U2FsdGVkX1')) {
          try {
            const decryptedLine = decryptData(line.trim());
            decryptedAddressLines.push(decryptedLine);
            fieldsDecrypted++;
            if (process.env.NODE_ENV === 'development') {
              console.log(`Decrypted address line: ${line.substring(0, 20)}... -> ${decryptedLine}`);
            }
          } catch (error) {
            console.warn(`Failed to decrypt address line:`, error);
            decryptedAddressLines.push(line); // Keep original if decryption fails
          }
        } else {
          // Non-encrypted line, keep as is
          decryptedAddressLines.push(line);
        }
      });
      
      // Rebuild the address with decrypted lines
      decrypted.address = decryptedAddressLines.filter(line => line && line.trim()).join('\n');
      console.log('Combined address field processed');
    }
    
    // Handle individual sensitive fields
    sensitiveFields.forEach(field => {
      if (decrypted[field] && typeof decrypted[field] === 'string') {
        try {
          // Only attempt decryption if the field looks encrypted (starts with U2FsdGVkX1)
          if (decrypted[field].startsWith('U2FsdGVkX1')) {
            const originalValue = decrypted[field];
            decrypted[field] = decryptData(decrypted[field]);
            fieldsDecrypted++;
            if (process.env.NODE_ENV === 'development') {
              console.log(`Decrypted ${field}: ${originalValue.substring(0, 20)}... -> ${decrypted[field]}`);
            }
          }
        } catch (error) {
          console.warn(`Failed to decrypt ${field}:`, error);
          // Keep original value if decryption fails
        }
      }
    });
    
    console.log(`Decrypted ${fieldsDecrypted} profile fields for PDF`);
    return decrypted;
  },

  // Generate Tax Report PDF
  generateTaxReport(taxSummary, invoices = [], expenses = [], userInfo = {}) {
    try {
      console.log('Starting PDF generation...');
      console.log('PDF generation - taxSummary received:', {
        taxYear: taxSummary.taxYear,
        income: taxSummary.income,
        invoiceCount: taxSummary.invoiceCount,
        hasInvoices: taxSummary.invoices ? taxSummary.invoices.length : 'undefined',
        totalInvoices: Array.isArray(taxSummary.invoices) ? taxSummary.invoices.length : 'not array'
      });
      
      // Decrypt user profile data before using it in the PDF
      const decryptedUserInfo = taxService.decryptUserProfile(userInfo);
      console.log('User info decrypted for PDF generation');
      
      // Create new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      let yPosition = margin;
      
      // Helper function to format currency
      const formatCurrency = (amount) => {
        const num = parseFloat(amount) || 0;
        return `£${num.toFixed(2)}`;
      };
      
      // Helper function to add a table with purple header
      const addTable = (title, data, y) => {
        const tableWidth = pageWidth - 2 * margin;
        const rowHeight = 8;
        const headerHeight = 10;
        
        // Title
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin, y);
        y += 15;
        
        // Header background (purple)
        doc.setFillColor(104, 79, 143);
        doc.rect(margin, y - headerHeight + 2, tableWidth, headerHeight, 'F');
        
        // Header text (white)
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Description', margin + 5, y);
        doc.text('Amount', pageWidth - margin - 5, y, { align: 'right' });
        y += rowHeight + 2;
        
        // Table rows
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        
        data.forEach((row, index) => {
          // Alternating row colors
          if (index % 2 === 0) {
            doc.setFillColor(248, 248, 248);
            doc.rect(margin, y - rowHeight + 2, tableWidth, rowHeight, 'F');
          }
          
          doc.text(row.description, margin + 5, y);
          doc.text(row.amount, pageWidth - margin - 5, y, { align: 'right' });
          y += rowHeight;
        });
        
        return y;
      };
      
      // Helper function to add monthly income table
      const addMonthlyTable = (title, monthlyData, y) => {
        const tableWidth = pageWidth - 2 * margin;
        const rowHeight = 8;
        const headerHeight = 10;
        
        // Title
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin, y);
        y += 15;
        
        // Header background (purple)
        doc.setFillColor(104, 79, 143);
        doc.rect(margin, y - headerHeight + 2, tableWidth, headerHeight, 'F');
        
        // Header text (white)
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Month', margin + 5, y);
        doc.text('Amount', pageWidth - margin - 5, y, { align: 'right' });
        y += rowHeight + 2;
        
        // Table rows
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        
        monthlyData.forEach((row, index) => {
          // Alternating row colors
          if (index % 2 === 0) {
            doc.setFillColor(248, 248, 248);
            doc.rect(margin, y - rowHeight + 2, tableWidth, rowHeight, 'F');
          }
          
          doc.text(row.month, margin + 5, y);
          doc.text(row.amount, pageWidth - margin - 5, y, { align: 'right' });
          y += rowHeight;
        });
        
        return y;
      };
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('SELF-EMPLOYMENT TAX SUMMARY', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;
      
      // User Information Section
      if (decryptedUserInfo.displayName || decryptedUserInfo.email) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Prepared for:', margin, yPosition);
        yPosition += 8;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        
        // User name
        if (decryptedUserInfo.displayName) {
          doc.text(decryptedUserInfo.displayName, margin, yPosition);
          yPosition += 6;
        }
        
        // User email
        if (decryptedUserInfo.email) {
          doc.text(decryptedUserInfo.email, margin, yPosition);
          yPosition += 6;
        }
        
        // Address if available
        if (decryptedUserInfo.address) {
          const addressLines = decryptedUserInfo.address.split('\n').filter(line => line.trim());
          addressLines.forEach(line => {
            doc.text(line.trim(), margin, yPosition);
            yPosition += 6;
          });
        }
        
        // Phone if available
        if (decryptedUserInfo.phone) {
          doc.text(`Tel: ${decryptedUserInfo.phone}`, margin, yPosition);
          yPosition += 6;
        }
        
        yPosition += 10; // Extra spacing after user info
      }
      
      // Basic info
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy')}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Tax Year: ${taxSummary.taxYear}`, margin, yPosition);
      yPosition += 6;
      
      if (taxSummary.taxYearStart && taxSummary.taxYearEnd) {
        const startDate = new Date(taxSummary.taxYearStart);
        const endDate = new Date(taxSummary.taxYearEnd);
        doc.text(`Period: ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`, margin, yPosition);
        yPosition += 15;
      }
      
      // HMRC Self-Assessment Data table
      const hmrcData = [
        { description: 'Turnover (Income)', amount: formatCurrency(taxSummary.income || 0) },
        { description: 'Expenses', amount: formatCurrency(taxSummary.expenses || 0) },
        { description: 'Net Profit', amount: formatCurrency(taxSummary.taxableIncome || 0) }
      ];
      
      yPosition = addTable('HMRC SELF-ASSESSMENT DATA', hmrcData, yPosition);
      yPosition += 20;
      
      // Tax Calculation Summary table
      const taxData = [
        { description: 'Income Tax Due', amount: formatCurrency(taxSummary.incomeTax || 0) },
        { description: 'Class 2 & 4 NI Contributions', amount: formatCurrency(taxSummary.nationalInsurance || 0) },
        { description: 'Total Tax Liability', amount: formatCurrency(taxSummary.totalTax || 0) },
        { description: 'Net Income After Tax', amount: formatCurrency(taxSummary.netIncome || 0) }
      ];
      
      yPosition = addTable('TAX CALCULATION SUMMARY', taxData, yPosition);
      yPosition += 20;
      
      // Get monthly breakdown from invoices if available
      const monthlyIncome = {};
      if (taxSummary.invoices && taxSummary.invoices.length > 0) {
        // Get tax year boundaries for filtering
        const { start: taxYearStart, end: taxYearEnd } = taxService.getTaxYearBoundaries(taxSummary.taxYear);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`Monthly breakdown - Tax year boundaries: ${taxYearStart.toISOString()} to ${taxYearEnd.toISOString()}`);
          console.log(`Monthly breakdown - Processing ${taxSummary.invoices.length} pre-filtered invoices`);
          console.log(`Monthly breakdown - Expected total income: £${taxSummary.income}`);
        }
        
        let monthlyTotal = 0;
        let entriesProcessed = 0;
        let entriesSkipped = 0;
        
        taxSummary.invoices.forEach(invoice => {
          if (invoice.summaryData && Array.isArray(invoice.summaryData)) {
            invoice.summaryData.forEach(entry => {
              if (entry.date && entry.amount) {
                let entryDate = null;
                if (typeof entry.date.toDate === 'function') {
                  entryDate = entry.date.toDate();
                } else if (entry.date instanceof Date) {
                  entryDate = entry.date;
                } else {
                  entryDate = new Date(entry.date);
                }
                
                // Only include entries that fall within the tax year (same logic as main calculation)
                if (entryDate && entryDate >= taxYearStart && entryDate <= taxYearEnd) {
                  const monthKey = format(entryDate, 'MMM yyyy');
                  const entryAmount = parseFloat(entry.amount) || 0;
                  monthlyIncome[monthKey] = (monthlyIncome[monthKey] || 0) + entryAmount;
                  monthlyTotal += entryAmount;
                  entriesProcessed++;
                  
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`Monthly breakdown: ${monthKey} += £${entry.amount} (date: ${entryDate.toISOString()})`);
                  }
                } else {
                  entriesSkipped++;
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`Monthly breakdown: Skipping entry outside tax year - date: ${entryDate ? entryDate.toISOString() : 'null'}, amount: £${entry.amount}`);
                  }
                }
              }
            });
          }
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Monthly income breakdown:', monthlyIncome);
          console.log(`Monthly breakdown totals: £${monthlyTotal.toFixed(2)} from ${entriesProcessed} entries (${entriesSkipped} entries skipped)`);
          console.log(`Difference from main calculation: £${(parseFloat(taxSummary.income) - monthlyTotal).toFixed(2)}`);
        }
      }
      
      // Monthly Income Summary table
      const monthlyData = Object.entries(monthlyIncome)
        .sort(([a], [b]) => {
          // Parse "MMM yyyy" format properly for sorting
          const dateA = new Date(Date.parse(a + " 01")); // Add day for parsing
          const dateB = new Date(Date.parse(b + " 01"));
          return dateA - dateB;
        })
        .map(([month, amount]) => ({
          month: month,
          amount: formatCurrency(amount)
        }));
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Monthly data for PDF table:', monthlyData);
        console.log('About to render monthly table with data:', JSON.stringify(monthlyData, null, 2));
      }
      
      if (monthlyData.length > 0) {
        console.log(`About to call addMonthlyTable at Y position: ${yPosition}, pageHeight: ${pageHeight}`);
        
        // Check if we have enough space for the monthly table
        // Need space for: title (15) + header (10) + at least 2 rows (16) + margin (20) = ~61mm
        const spaceNeeded = 61;
        const spaceAvailable = pageHeight - yPosition - margin;
        
        console.log(`Space needed: ${spaceNeeded}mm, space available: ${spaceAvailable}mm`);
        
        if (spaceAvailable < spaceNeeded) {
          console.log('Not enough space for monthly table, adding new page');
          doc.addPage();
          yPosition = margin;
        }
        
        console.log('Calling addMonthlyTable with data:', monthlyData);
        yPosition = addMonthlyTable('MONTHLY INCOME SUMMARY', monthlyData, yPosition);
        console.log(`After addMonthlyTable, Y position is: ${yPosition}`);
        yPosition += 20;
      }
      
      // Check if we need a new page for expenses
      if (expenses && expenses.length > 0) {
        // Check if we have enough space for expenses section
        const spaceNeeded = 80; // Conservative estimate for expenses section
        const spaceAvailable = pageHeight - yPosition - margin;
        
        console.log(`Expenses: Space needed: ${spaceNeeded}mm, space available: ${spaceAvailable}mm`);
        
        if (spaceAvailable < spaceNeeded) {
          console.log('Not enough space for expenses section, adding new page');
          doc.addPage();
          yPosition = margin;
        }
      }
      
      // Expenses Summary
      if (expenses && expenses.length > 0) {
        const expensesByCategory = {};
        expenses.forEach(expense => {
          const category = expense.category || 'Other expenses';
          const amount = parseFloat(expense.totalAmount) || 0;
          expensesByCategory[category] = (expensesByCategory[category] || 0) + amount;
        });
        
        const expenseSummaryData = Object.entries(expensesByCategory).map(([category, amount]) => ({
          description: category,
          amount: formatCurrency(amount)
        }));
        
        // Add total row
        expenseSummaryData.push({
          description: 'TOTAL EXPENSES',
          amount: formatCurrency(taxSummary.expenses || 0)
        });
        
        yPosition = addTable('EXPENSES SUMMARY', expenseSummaryData, yPosition);
        yPosition += 20;
        
        // Detailed Expenses table - show all expenses
        const detailedExpenses = expenses.map(expense => {
          const date = expense.date ? format(expense.date.toDate ? expense.date.toDate() : new Date(expense.date), 'dd/MM/yyyy') : 'N/A';
          return {
            date: date,
            description: expense.description || 'N/A',
            category: expense.category || 'Other expenses',
            amount: formatCurrency(parseFloat(expense.totalAmount) || 0)
          };
        });
        
        console.log(`Preparing to render ${detailedExpenses.length} detailed expenses`);
        
        if (detailedExpenses.length > 0) {
          // Check if we have enough space for the detailed expenses table header
          const headerSpace = 25; // Title + header
          if (yPosition + headerSpace > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          
          // Title
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('DETAILED EXPENSES', margin, yPosition);
          yPosition += 15;
          
          // Table header
          const tableWidth = pageWidth - 2 * margin;
          const rowHeight = 8;
          const headerHeight = 10;
          
          doc.setFillColor(104, 79, 143);
          doc.rect(margin, yPosition - headerHeight + 2, tableWidth, headerHeight, 'F');
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Date', margin + 5, yPosition);
          doc.text('Description', margin + 35, yPosition);
          doc.text('Category', margin + 110, yPosition);
          doc.text('Amount', pageWidth - margin - 5, yPosition, { align: 'right' });
          yPosition += rowHeight + 2;
          
          // Table rows with page break checking
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');
          
          detailedExpenses.forEach((expense, index) => {
            // Check if we need a new page for this row
            if (yPosition + rowHeight > pageHeight - margin) {
              console.log(`Adding new page for expense row ${index}`);
              doc.addPage();
              yPosition = margin;
              
              // Re-add the table header on the new page
              doc.setFillColor(104, 79, 143);
              doc.rect(margin, yPosition - headerHeight + 2, tableWidth, headerHeight, 'F');
              
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(10);
              doc.setFont('helvetica', 'bold');
              doc.text('Date', margin + 5, yPosition);
              doc.text('Description', margin + 35, yPosition);
              doc.text('Category', margin + 110, yPosition);
              doc.text('Amount', pageWidth - margin - 5, yPosition, { align: 'right' });
              yPosition += rowHeight + 2;
              
              // Reset text color for data rows
              doc.setTextColor(0, 0, 0);
              doc.setFont('helvetica', 'normal');
            }
            
            // Alternating row colors
            if (index % 2 === 0) {
              doc.setFillColor(248, 248, 248);
              doc.rect(margin, yPosition - rowHeight + 2, tableWidth, rowHeight, 'F');
            }
            
            doc.text(expense.date, margin + 5, yPosition);
            doc.text(expense.description.substring(0, 25), margin + 35, yPosition);
            doc.text(expense.category.substring(0, 20), margin + 110, yPosition);
            doc.text(expense.amount, pageWidth - margin - 5, yPosition, { align: 'right' });
            yPosition += rowHeight;
          });
        }
      }
      
      console.log('PDF generation completed successfully');
      return doc;
      
    } catch (error) {
      console.error('Error generating tax report PDF:', error);
      throw new Error(`Failed to generate tax report: ${error.message}`);
    }
  }
};
